import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getInternalUsers, setUserTeam, setUserArchived, setUserRole, inviteInternalUser, type InternalUser, type ProspectingTeam } from '../../lib/refonteApi';
import { useAuth } from '../../components/AuthProvider';
import { SkeletonRows } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';

const ROLE_LABEL: Record<string, string> = { sdr: 'SDR', csm: 'CSM', admin: 'Interne', super_admin: 'Direction' };
const ROLE_OPTIONS = [{ v: 'sdr', l: 'SDR' }, { v: 'csm', l: 'CSM' }, { v: 'admin', l: 'Interne' }, { v: 'super_admin', l: 'Direction' }];
const TEAMS: { v: ProspectingTeam; l: string; c: string }[] = [
  { v: 'cold_call', l: 'Cold call', c: 'bg-hook-rose text-white border-hook-rose' },
  { v: 'gtm', l: 'GTM', c: 'bg-st-ok text-white border-st-ok' },
  { v: null, l: 'Renfort', c: 'bg-ink text-white border-ink' },
];

// Gestion de l'équipe : qui prospecte en cold call, qui en GTM, qui est en renfort (bonus,
// hors capacité vendable). Cette affectation pilote l'agenda et le calcul de capacité.
const TeamManagementPage: React.FC = () => {
  const { profile } = useAuth();
  const canManageRoles = profile?.role === 'super_admin'; // Direction seulement
  const canInvite = ['admin', 'csm', 'super_admin'].includes(profile?.role || '');
  // Invitation d'un nouvel interne : compte + rôle + équipe en une fois. Il se connecte ensuite
  // avec Google (adresse hook-agency.com) ou par code — voir /app/aide.
  const [invOpen, setInvOpen] = useState(false);
  const [inv, setInv] = useState({ email: '', first: '', last: '', role: 'sdr' as 'sdr' | 'csm' | 'admin', team: 'cold_call' as ProspectingTeam });
  const [inviting, setInviting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [users, setUsers] = useState<InternalUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try { setUsers(await getInternalUsers()); } catch (e: any) { setErr(e.message); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const assign = async (u: InternalUser, team: ProspectingTeam) => {
    if (u.team === team) return;
    setBusy(u.userId);
    setUsers(us => us.map(x => (x.userId === u.userId ? { ...x, team } : x)));
    await setUserTeam(u.userId, team).catch(() => load());
    setBusy(null);
  };
  const archive = async (u: InternalUser, archived: boolean) => {
    setBusy(u.userId);
    setUsers(us => us.map(x => (x.userId === u.userId ? { ...x, archived } : x)));
    await setUserArchived(u.userId, archived).catch(() => load());
    setBusy(null);
  };
  const changeRole = async (u: InternalUser, role: string) => {
    if (u.role === role) return;
    setBusy(u.userId);
    setUsers(us => us.map(x => (x.userId === u.userId ? { ...x, role } : x)));
    await setUserRole(u.userId, role).catch(() => load());
    setBusy(null);
  };

  const submitInvite = async () => {
    const email = inv.email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setErr('Email invalide.'); return; }
    setInviting(true); setErr(null); setNotice(null);
    try {
      const uid = await inviteInternalUser(email, inv.role, inv.first.trim() || undefined, inv.last.trim() || undefined);
      if (uid) await setUserTeam(uid, inv.team);
      setNotice(uid
        ? `Compte créé pour ${email}. Il reçoit un mail « Ton accès Hook est prêt » : connexion avec Google (adresse hook-agency.com) ou par code.`
        : `${email} avait déjà un compte : rien renvoyé, vérifie son rôle et son équipe ci-dessous.`);
      setInv({ email: '', first: '', last: '', role: 'sdr', team: 'cold_call' });
      setInvOpen(false);
      load();
    } catch (e: any) { setErr(e.message); }
    setInviting(false);
  };

  const present = users.filter(u => !u.archived);
  const gone = users.filter(u => u.archived);
  const counts = useMemo(() => ({
    cold_call: present.filter(u => u.team === 'cold_call').length,
    gtm: present.filter(u => u.team === 'gtm').length,
    renfort: present.filter(u => u.team == null).length,
  }), [present]);

  const groups: { key: string; label: string; list: InternalUser[]; tone: string }[] = [
    { key: 'cold_call', label: 'Équipe Cold call', list: present.filter(u => u.team === 'cold_call'), tone: 'text-hook-rose' },
    { key: 'gtm', label: 'Équipe GTM', list: present.filter(u => u.team === 'gtm'), tone: 'text-st-ok' },
    { key: 'renfort', label: 'Renfort / management (hors capacité)', list: present.filter(u => u.team == null), tone: 'text-dim' },
  ];

  return (
    <div>
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
        <h1 className="text-[21px] font-bold tracking-tight">Équipe & rôles</h1>
        <span className="font-mono text-[11px] text-faint">{users.length} internes · {counts.cold_call} CC · {counts.gtm} GTM · {counts.renfort} renfort</span>
      </div>
      <p className="text-dim text-[13px] mb-3">Assigne chaque personne à une équipe de prospection. Le <b>renfort</b> (Kevin, Baptiste, management…) n'entre pas dans la capacité vendable — c'est de la marge. Cette affectation pilote l'agenda.</p>

      {canInvite && (
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <button onClick={() => setInvOpen(v => !v)} className="text-[12.5px] font-semibold px-3 h-9 rounded-lg bg-ink text-white hover:opacity-90">
            {invOpen ? 'Fermer' : '＋ Inviter un interne'}
          </button>
          <Link to="/app/aide" className="text-[12.5px] text-hook-rose font-semibold hover:underline">Guide : onboarder un SDR →</Link>
        </div>
      )}
      {notice && <div className="mb-4 text-[12.5px] text-st-ok bg-st-ok/10 border border-st-ok/20 rounded-lg px-3 py-2">{notice}</div>}
      {invOpen && (
        <div className="mb-4 bg-panel border border-line rounded-card p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-hook-rose mb-3">Inviter un interne</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <input placeholder="Email hook-agency.com *" type="email" value={inv.email} onChange={e => setInv(s => ({ ...s, email: e.target.value }))} className="text-[13px] rounded-lg border border-line-2 bg-panel px-3 h-9" />
            <input placeholder="Prénom" value={inv.first} onChange={e => setInv(s => ({ ...s, first: e.target.value }))} className="text-[13px] rounded-lg border border-line-2 bg-panel px-3 h-9" />
            <input placeholder="Nom" value={inv.last} onChange={e => setInv(s => ({ ...s, last: e.target.value }))} className="text-[13px] rounded-lg border border-line-2 bg-panel px-3 h-9" />
            <select value={inv.role} onChange={e => setInv(s => ({ ...s, role: e.target.value as any }))} className="text-[13px] rounded-lg border border-line-2 bg-panel px-2 h-9">
              <option value="sdr">SDR</option><option value="csm">CSM</option><option value="admin">Interne</option>
            </select>
            <select value={inv.team ?? 'renfort'} onChange={e => setInv(s => ({ ...s, team: e.target.value === 'renfort' ? null : (e.target.value as ProspectingTeam) }))} className="text-[13px] rounded-lg border border-line-2 bg-panel px-2 h-9">
              <option value="cold_call">Équipe Cold call</option><option value="gtm">Équipe GTM</option><option value="renfort">Renfort / management</option>
            </select>
          </div>
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <button disabled={inviting || !inv.email.trim()} onClick={submitInvite}
              className="text-[12.5px] font-semibold px-3 h-9 rounded-lg bg-hook-rose text-white hover:opacity-90 disabled:opacity-40">
              {inviting ? 'Envoi…' : "Créer le compte"}</button>
            <button onClick={() => setInvOpen(false)} className="text-[12.5px] px-3 h-9 rounded-lg border border-line-2 hover:border-ink">Annuler</button>
            <span className="text-[11.5px] text-dim">Il reçoit un mail avec la marche à suivre. Avec une adresse hook-agency.com, « Continuer avec Google » suffit.</span>
          </div>
        </div>
      )}

      {err && <EmptyState tone="error" title="Chargement impossible" hint={err} className="mb-4" />}
      {loading ? <SkeletonRows count={10} /> : (
        <div className="space-y-6">
          {groups.map(g => (
            <section key={g.key}>
              <h2 className={`font-mono text-[10px] uppercase tracking-widest mb-2 ${g.tone}`}>{g.label} · {g.list.length}</h2>
              {g.list.length === 0 ? (
                <p className="text-faint text-[12px] italic mb-1">Personne pour l'instant.</p>
              ) : (
                <div className="bg-panel border border-line rounded-card divide-y divide-line">
                  {g.list.map(u => (
                    <div key={u.userId} className="flex items-center gap-3 px-3.5 py-2.5">
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-semibold flex items-center gap-2">
                          {u.name}
                          {u.active && <span className="inline-flex items-center gap-1 font-mono text-[8.5px] text-st-ok"><span className="w-1.5 h-1.5 rounded-full bg-st-ok" />actif</span>}
                        </div>
                        <div className="font-mono text-[10px] text-faint">{ROLE_LABEL[u.role] || u.role}{u.squad ? ` · ${u.squad}` : ''}</div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {canManageRoles && (
                          <select value={u.role} disabled={busy === u.userId || u.userId === profile?.user_id} onChange={e => changeRole(u, e.target.value)}
                            title={u.userId === profile?.user_id ? 'Ton propre rôle (non modifiable ici)' : 'Droits (rôle)'}
                            className="text-[11px] rounded-md border border-line-2 bg-panel px-1.5 py-1 mr-1 text-dim disabled:opacity-50">
                            {ROLE_OPTIONS.map(r => <option key={r.v} value={r.v}>{r.l}</option>)}
                          </select>
                        )}
                        {TEAMS.map(t => {
                          const on = u.team === t.v;
                          return (
                            <button key={String(t.v)} disabled={busy === u.userId} onClick={() => assign(u, t.v)}
                              className={`text-[11px] font-semibold px-2.5 py-1 rounded-md border transition-colors disabled:opacity-50 ${on ? t.c : 'border-line-2 text-dim hover:border-ink'}`}>
                              {t.l}
                            </button>
                          );
                        })}
                        <button disabled={busy === u.userId} onClick={() => archive(u, true)} title="Marquer comme parti"
                          className="ml-1 text-[15px] leading-none text-faint hover:text-st-bad px-1 disabled:opacity-50" aria-label="Marquer comme parti">×</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))}

          {gone.length > 0 && (
            <section>
              <h2 className="font-mono text-[10px] uppercase tracking-widest text-faint mb-2">Anciens (archivés) · {gone.length}</h2>
              <div className="bg-panel border border-line rounded-card divide-y divide-line opacity-70">
                {gone.map(u => (
                  <div key={u.userId} className="flex items-center gap-3 px-3.5 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-[12.5px] font-medium line-through decoration-faint">{u.name}</div>
                      <div className="font-mono text-[10px] text-faint">{ROLE_LABEL[u.role] || u.role}{u.squad ? ` · ${u.squad}` : ''}</div>
                    </div>
                    <button disabled={busy === u.userId} onClick={() => archive(u, false)}
                      className="text-[11px] font-semibold px-2.5 py-1 rounded-md border border-line-2 text-dim hover:border-ink disabled:opacity-50">Réintégrer</button>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamManagementPage;
